'use client';
import SearchAutocomplete from './_SearchAutocomplete';
import { prependSlash } from '@repo/ui/src/components/Anchor/helpers';
import SearchIcon from '@repo/ui/src/components/Icons/SearchIcon';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { classNames } from '@repo/ui/src/utils/classNames';
import React, { useEffect } from 'react';

export interface SearchProps {
  link?: string;
  placeholder?: string;
  searchCategory?: SearchCategory;
}

export enum SearchCategory {
  all = 'None',
  products = 'Product',
  recipes = 'Recipe',
  learn = 'Learn',
  support = 'Support',
}

const getSearchTerm = (form: HTMLFormElement) => new FormData(form).get('searchTerm') as string;

const Search = ({
  link = 'search',
  placeholder = 'Search',
  searchCategory = SearchCategory.all,
}: SearchProps) => {
  const { push } = useCurrentUrl();
  const ref = React.useRef<HTMLInputElement>(null);
  const autocompleteRef = React.useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { currentTarget } = e;
    setIsOpen((curr) => {
      if (curr) {
        handleSearch(getSearchTerm(currentTarget.form!));
        return curr;
      }
      return !curr;
    });
  };

  useEffect(() => {
    if (isOpen) {
      ref.current?.focus();
    }
  }, [isOpen]);

  const handleSearch = async (term: string) => {
    const queryParams = new URLSearchParams({
      term,
    });
    if (searchCategory !== SearchCategory.all) {
      queryParams.set('type', searchCategory);
    }
    const url = `${prependSlash(link)}?${queryParams}`;
    setIsOpen(false);
    ref.current!.value = term;
    await push(url);

    setSearchTerm('');
    ref.current!.value = '';
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        return handleSearch(getSearchTerm(e.currentTarget));
      }}
      className="relative hidden lg:grid items-center"
    >
      <input
        type="text"
        name="searchTerm"
        placeholder={placeholder}
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls="global-nav-autocomplete"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.currentTarget.blur();
          }

          if (e.key === 'ArrowDown') {
            e.preventDefault();
            autocompleteRef.current?.focus();
          }
        }}
        onChange={(e) => setSearchTerm(e.currentTarget.value)}
        onBlur={(e) => {
          const { currentTarget, relatedTarget } = e;
          if (!currentTarget.form?.contains(relatedTarget)) {
            setTimeout(() => {
              setIsOpen(false);
            }, 100);
          }
        }}
        className={classNames(
          'rounded-[80px] border border-neutral-300 bg-transparent py-2 px-8 tra-text-web-body-l text-white focus:outline-brand-200 focus:outline-2 focus-visible:outline',
          isOpen
            ? 'w-[15vw] opacity-1 [transition:opacity_200ms,width_250ms]'
            : 'w-0 opacity-0 [transition:opacity_200ms_100ms,width_250ms_100ms] pointer-events-none',
        )}
        ref={ref}
      />
      <button
        aria-label={placeholder}
        type="button"
        className="w-6 text-white absolute right-5 inset-y-2 cursor-pointer"
        onClick={handleOpen}
        onBlur={(e) => {
          const { currentTarget, relatedTarget } = e;
          if (!currentTarget.form?.contains(relatedTarget)) {
            setIsOpen(false);
          }
        }}
      >
        <SearchIcon />
      </button>
      {searchCategory === SearchCategory.all && searchTerm && (
        <SearchAutocomplete
          searchTerm={searchTerm}
          search={handleSearch}
          className={classNames(
            'duration-150',
            isOpen ? 'opacity-1 delay-300' : 'opacity-0 pointer-events-none translate-y-3',
          )}
          id="global-nav-autocomplete"
          ref={autocompleteRef}
          returnFocus={() => {
            ref.current?.focus();
          }}
        />
      )}
    </form>
  );
};

export default Search;
